/**
 * Authentication redirect URL builders for different brands.
 * Migrated from @zg-rentals/particles-js-auth pathsManifest.
 */

type RedirectFunction = (toUrl: string, otherParams: string) => string;

type ZillowRedirectFunction = (toUrl: string, otherParams: string, customLoginPath?: string) => string;

type BrandManifest = {
  signInPage: RedirectFunction;
  passwordResetPage: RedirectFunction;
  twoFactorAuthPage: RedirectFunction;
};

type ZillowManifest = {
  signInPage: ZillowRedirectFunction;
  nativeAppSignInPage: RedirectFunction;
  passwordResetPage: RedirectFunction;
  twoFactorAuthPage: RedirectFunction;
};

type TruliaManifest = {
  signInPage: RedirectFunction;
  passwordResetPage: RedirectFunction;
  twoFactorAuthPage: (toUrl: string, otherParams: string) => void;
};

type AuthRedirectsManifest = {
  hotpads: BrandManifest;
  zillow: ZillowManifest;
  trulia: TruliaManifest;
};

/**
 * Build query string from params object without encoding.
 * Replicates qs.stringify(params, { encode: false }) behavior.
 */
function buildParams(params: Record<string, string | boolean>): string {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

/**
 * Build URL with optional query string.
 */
function buildUrl(url: string, querystring?: string): string {
  if (!querystring) {
    return url;
  }

  return `${url}?${querystring}`;
}

/**
 * Remove leading '?' from query string if present.
 */
function cleanParams(otherParams: string): string {
  if (typeof otherParams === 'string' && otherParams.charAt(0) === '?') {
    return otherParams.substring(1);
  }

  return otherParams;
}

/**
 * Parse query string into params object.
 * Replicates qs.parse() behavior for simple use cases.
 */
function parseParams(queryString: string): Record<string, string> {
  const params: Record<string, string> = {};
  const cleaned = cleanParams(queryString);

  if (!cleaned) {
    return params;
  }

  const pairs = cleaned.split('&');
  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key) {
      params[key] = value || '';
    }
  }

  return params;
}

/**
 * Extract the first path segment after leading slash.
 */
function getProjectBase(pathname = '/'): string {
  const pathnameArray = pathname.split('/');

  if (pathnameArray.length === 1 || pathname[0] !== '/') {
    return pathnameArray[0];
  } else {
    return pathnameArray[1];
  }
}

/**
 * Customize sign-in page parameters based on the flow.
 * Adds custom headers/subheaders for Apply Now, Applications, and Payments flows.
 */
function customizeSignIn(otherParams: string, paramsToBuild: Record<string, string>): Record<string, string> {
  if (otherParams.includes('utm_campaign=applynow') || otherParams.includes('inviteId')) {
    // If user comes from clicking "Apply Now" or invitation url, we want to customize the sign in page
    paramsToBuild['he'] = encodeURIComponent('Apply to this property');
    paramsToBuild['sh'] = encodeURIComponent('Please sign in or create an account to securely apply.');
  } else if (
    paramsToBuild.url.match('rental-manager%2Fproperties%2F.+%2Fapplications') ||
    paramsToBuild.url.match('renter-hub%2Fapplications')
  ) {
    paramsToBuild['he'] = encodeURIComponent('Continue to Applications');
    paramsToBuild['sh'] = encodeURIComponent('Please sign in to securely manage your rental applications.');
  } else if (
    paramsToBuild.url.match('rental-manager%2Fproperties%2F.+%2Fpayments') ||
    paramsToBuild.url.match('rental-manager%2Fpayments') ||
    paramsToBuild.url.match('renter-hub%2Fpayments')
  ) {
    paramsToBuild['he'] = encodeURIComponent('Continue to Payments');
    paramsToBuild['sh'] = encodeURIComponent('Please sign in to securely manage your rental payments.');
  }

  return paramsToBuild;
}

/**
 * Customize password reset parameters for mobile app context.
 */
function customizePasswordReset(
  otherParams: string,
  paramsToBuild: Record<string, string | boolean>,
): Record<string, string | boolean> {
  const params = parseParams(otherParams);
  const isZillowApp = params.isZillowApp;
  if (isZillowApp === 'true') {
    paramsToBuild.fromApp = true;
  }

  return paramsToBuild;
}

/**
 * Authentication redirect URL builders for each brand.
 * Provides sign-in, password reset, and two-factor auth page URLs.
 */
export default {
  hotpads: {
    signInPage: (toUrl, otherParams) => {
      const params = parseParams(cleanParams(otherParams));
      return buildUrl(
        '/login',
        buildParams({
          redirect: toUrl,
          ...params,
        }),
      );
    },
    passwordResetPage: (toUrl, otherParams) => {
      const params = parseParams(cleanParams(otherParams));
      return buildUrl(
        '/user/update-password',
        buildParams({
          redirect: toUrl,
          ...params,
        }),
      );
    },
    twoFactorAuthPage: (toUrl, otherParams) => {
      const params = parseParams(cleanParams(otherParams));
      const projectBase = getProjectBase(toUrl);

      return buildUrl(
        `/${projectBase}/2fa`,
        buildParams({
          redirect: toUrl,
          ...params,
        }),
      );
    },
  },

  zillow: {
    signInPage: (toUrl, otherParams, zillowCustomLoginPagePath = '') => {
      const paramsToBuild: Record<string, string> = {
        url: encodeURIComponent(toUrl + otherParams),
      };
      const params = buildParams(customizeSignIn(otherParams, paramsToBuild));
      return buildUrl(zillowCustomLoginPagePath || '/user/acct/login/', params);
    },
    nativeAppSignInPage: (toUrl, otherParams) => {
      const params = buildParams({
        url: encodeURIComponent(toUrl + otherParams),
      });
      return buildUrl('/renter-hub/applications/nativeAppSignInPage', params);
    },
    passwordResetPage: (toUrl, otherParams) => {
      const paramsToBuild: Record<string, string | boolean> = {
        url: encodeURIComponent(toUrl + otherParams),
      };
      const params = buildParams(customizePasswordReset(otherParams, paramsToBuild));
      return buildUrl('/myzillow/ChangePassword.htm', params);
    },
    twoFactorAuthPage: (toUrl, otherParams) => {
      const paramsToBuild: Record<string, string> = {
        url: encodeURIComponent(toUrl + otherParams),
      };
      const params = buildParams(customizeSignIn(otherParams, paramsToBuild));
      return buildUrl('/user/acct/login/', params);
    },
  },

  trulia: {
    signInPage: (toUrl, otherParams) => buildUrl(`/login/r${toUrl}`, cleanParams(otherParams)),
    passwordResetPage: (toUrl, otherParams) => buildUrl(`/login/r${toUrl}`, cleanParams(otherParams)),
    twoFactorAuthPage: (toUrl, otherParams) => {
      const params = buildParams({
        redirect: encodeURIComponent(toUrl + otherParams),
      });
      const projectBase = getProjectBase(toUrl);

      // Note: Original implementation missing return statement - preserving for exact parity
      buildUrl(`/${projectBase}/2fa`, params);
    },
  },
} satisfies AuthRedirectsManifest;
